import { createReducer } from '@reduxjs/toolkit';

import type { Product, Purchase, Subscription } from '../../services/iap';
import { Package } from '../types';
import {
	iapDisconnect,
	iapFetchProductsFailure,
	iapFetchProductsRequest,
	iapFetchProductsSuccess,
	iapFetchSubscriptionsFailure,
	iapFetchSubscriptionsRequest,
	iapFetchSubscriptionsSuccess,
	iapInitFailure,
	iapInitRequest,
	iapInitSuccess,
	iapPurchaseError,
	iapPurchaseFailure,
	iapPurchaseRequest,
	iapPurchaseSuccess,
	iapPurchaseUpdate,
	iapReset,
	iapVerifyPurchaseFailure,
	iapVerifyPurchaseRequest,
	iapVerifyPurchaseSuccess,
} from './iapActions';

export interface IAPState {
	// Initialization
	isInitialized: boolean;
	initLoading: boolean;
	initError?: string;

	// Products
	products: Product[];
	productsLoading: boolean;
	productsError?: string;

	// Subscriptions
	subscriptions: Subscription[];
	subscriptionsLoading: boolean;
	subscriptionsError?: string;

	// Purchase
	purchaseLoading: boolean;
	purchaseError?: string;
	currentPurchase?: Purchase;
	currentPackage?: Package;
	/** The donation recipient of the purchase in flight; verified with it, then cleared. */
	currentRecipientPlayerId?: string;

	// Verification
	verifyLoading: boolean;
	verifyError?: string;
	purchaseVerified: boolean;
	newBalance?: number;

	// Purchase history
	pendingPurchases: Purchase[];
}

const initialState: IAPState = {
	isInitialized: false,
	initLoading: false,
	products: [],
	productsLoading: false,
	subscriptions: [],
	subscriptionsLoading: false,
	purchaseLoading: false,
	verifyLoading: false,
	purchaseVerified: false,
	pendingPurchases: [],
};

export const iapReducer = createReducer(initialState, builder => {
	// Initialize
	builder
		.addCase(iapInitRequest, state => {
			state.initLoading = true;
			state.initError = undefined;
		})
		.addCase(iapInitSuccess, state => {
			state.initLoading = false;
			state.isInitialized = true;
			state.initError = undefined;
		})
		.addCase(iapInitFailure, (state, action) => {
			state.initLoading = false;
			state.isInitialized = false;
			state.initError = action.payload;
		})
		.addCase(iapDisconnect, state => {
			state.isInitialized = false;
			state.initLoading = false;
		});

	// Fetch products
	builder
		.addCase(iapFetchProductsRequest, state => {
			state.productsLoading = true;
			state.productsError = undefined;
		})
		.addCase(iapFetchProductsSuccess, (state, action) => {
			state.productsLoading = false;
			state.products = action.payload;
			state.productsError = undefined;
		})
		.addCase(iapFetchProductsFailure, (state, action) => {
			state.productsLoading = false;
			state.productsError = action.payload;
		});

	// Fetch subscriptions
	builder
		.addCase(iapFetchSubscriptionsRequest, state => {
			state.subscriptionsLoading = true;
			state.subscriptionsError = undefined;
		})
		.addCase(iapFetchSubscriptionsSuccess, (state, action) => {
			state.subscriptionsLoading = false;
			state.subscriptions = action.payload;
			state.subscriptionsError = undefined;
		})
		.addCase(iapFetchSubscriptionsFailure, (state, action) => {
			state.subscriptionsLoading = false;
			state.subscriptionsError = action.payload;
		});

	// Purchase
	builder
		.addCase(iapPurchaseRequest, (state, action) => {
			state.purchaseLoading = true;
			state.purchaseError = undefined;
			state.currentPurchase = undefined;
			state.currentPackage = action.payload.pkg;
			state.currentRecipientPlayerId = action.payload.recipientPlayerId;
			state.verifyError = undefined;
			state.newBalance = undefined;
			state.purchaseVerified = false;
		})
		.addCase(iapPurchaseSuccess, (state, action) => {
			state.purchaseLoading = false;
			state.currentPurchase = action.payload.purchase;
			state.currentPackage = action.payload.pkg;
			state.purchaseError = undefined;
		})
		.addCase(iapPurchaseFailure, (state, action) => {
			state.purchaseLoading = false;
			state.purchaseError = action.payload;
		})
		.addCase(iapPurchaseUpdate, (state, action) => {
			state.purchaseLoading = false;
			state.currentPurchase = action.payload;
			state.pendingPurchases.push(action.payload);
		})
		.addCase(iapPurchaseError, (state, action) => {
			state.purchaseLoading = false;
			state.purchaseError = action.payload.message || 'Purchase failed';
		});

	// Verify purchase
	builder
		.addCase(iapVerifyPurchaseRequest, state => {
			state.verifyLoading = true;
			state.verifyError = undefined;
			state.purchaseVerified = false;
		})
		.addCase(iapVerifyPurchaseSuccess, (state, action) => {
			state.purchaseLoading = false;
			state.verifyLoading = false;
			state.purchaseVerified = true;
			state.newBalance = action.payload.newBalance;
			state.verifyError = undefined;
			state.currentPurchase = undefined;
			state.currentPackage = undefined;
			state.currentRecipientPlayerId = undefined;
			// Remove from pending
			state.pendingPurchases = state.pendingPurchases.filter(
				p => p.transactionId !== action.payload.purchase.transactionId,
			);
		})
		.addCase(iapVerifyPurchaseFailure, (state, action) => {
			state.purchaseLoading = false;
			state.verifyLoading = false;
			state.verifyError = action.payload.error;
			state.purchaseVerified = false;
			state.currentPurchase = undefined;
			state.currentPackage = undefined;
			state.currentRecipientPlayerId = undefined;
			state.pendingPurchases = state.pendingPurchases.filter(
				p => p.transactionId !== action.payload.purchase.transactionId,
			);
		});

	// Reset
	builder.addCase(iapReset, state => {
		state.purchaseLoading = false;
		state.purchaseError = undefined;
		state.currentPurchase = undefined;
		state.currentPackage = undefined;
		state.currentRecipientPlayerId = undefined;
		state.verifyLoading = false;
		state.verifyError = undefined;
		state.purchaseVerified = false;
		state.newBalance = undefined;
	});
});
